import '../Components/App.css';
import 'react-toastify/dist/ReactToastify.css';

import snte from '../Resources/logoSNTE.svg';
import mtroLogo from '../Resources/LogoLeonel.svg';

import { ToastContainer, toast } from 'react-toastify';
import React, { Suspense, useEffect, useState } from 'react';
import { Message } from './message.jsx';
import { getAuthToken, searchUserByRfc, fetchCatalogs, createNewUser } from '../services/api.js';

const Instructions = React.lazy(() => import('./Instructions'));
const PrivacyPolicy = React.lazy(() => import('./PrivacyPolicy'));
const AddRecordForm = React.lazy(() => import('./AddRecordForm'));

function App() {
  const [rfc, setRfc] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState({ type: '', text: '', title: '', showContactInfo: false });
  const [showInstructions, setShowInstructions] = useState(true);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const [catalogs, setCatalogs] = useState({
    municipios: [],
    regiones: [],
    niveles: [],
  });

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const consultToken = await getAuthToken('consult');
        const catalogData = await fetchCatalogs(consultToken);
        setCatalogs(catalogData);
      } catch (error) {
        toast.error(error.message || 'Error al cargar datos iniciales.');
      }
    };

    loadCatalogs();

    // Precarga de logos (el fondo se carga via CSS)
    const images = [snte, mtroLogo];
    images.forEach((imageSrc) => {
      const img = new Image();
      img.src = imageSrc;
    });
  }, []);

  const handleModalClose = () => setMessageOpen(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!rfc || rfc.length < 12 || rfc.length > 18) {
      toast.warn('Por favor, ingresa un RFC (12 o 13 caracteres) o CURP (18 caracteres) válido.');
      return;
    }
    setIsLoading(true);
    setModalMessage({ type: '', text: '', title: '', showContactInfo: false });

    try {
      const consultToken = await getAuthToken('consult');
      const rfcResponse = await searchUserByRfc(rfc, consultToken);

      if (rfcResponse.folio === 0) {
        setModalMessage({
          type: 'error',
          title: 'Usuario No Encontrado',
          text: 'Aun no te encuentras registrado para el sorteo.',
          showContactInfo: true,
        });
        setMessageOpen(true);
      } else {
        const folioFormatted = rfcResponse.folio.toString().padStart(5, '0');
        setModalMessage({
          type: 'success',
          title: '¡Registro Encontrado!',
          text: `Ya estás registrado al sorteo. Tu numero de participación es: ${folioFormatted}`,
          //text: 'Ya te encuentras registrado, tu folio se te sera asignado el proximo lunes 06 de Julio.',
          showContactInfo: false,
        });
        setMessageOpen(true);
        setRfc('');
      }
    } catch (error) {
      let errorMessage = 'Ocurrió un error al buscar tu registro.';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') errorMessage = error.response.data;
      } else if (error.request) {
        errorMessage = 'No se pudo conectar con el servidor.';
      }
      setModalMessage({ type: 'error', title: 'Error de Búsqueda', text: errorMessage, showContactInfo: true });
      setMessageOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const createToken = await getAuthToken('create');
      await createNewUser(formData, createToken);
      setIsFormOpen(false);
      toast.success('¡Registro completado con éxito!');
    } catch (error) {
      let errorMessage = 'Hubo un problema al guardar tu registro. Intenta de nuevo.';
      if (error.response?.data) {
        const d = error.response.data;
        if (typeof d === 'string') errorMessage = d;
        else if (d.errors) errorMessage = Object.values(d.errors).flat().join(' \n');
        else if (d.title) errorMessage = d.title;
        else { try { errorMessage = JSON.stringify(d); } catch (_) {} }
      } else if (error.request) {
        errorMessage = 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
      } else {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => { if (event.key === 'Enter') handleSubmit(event); };
  const handleChange  = (e) => setRfc(e.target.value);

  return (
    <div className="app-container">

      {/* Botón flotante de información */}
      <button
        className="instructions-trigger"
        onClick={() => setShowInstructions(true)}
        title="Ver instrucciones"
        aria-label="Ver instrucciones para el registro"
      />

      {/* Botón flotante de registro (comentado igual que antes) */}
      {/*
      <button onClick={() => setIsFormOpen(true)} className="add-record-button" type="button" title="Registrarme" aria-label="Registrarme al sorteo">
        <span>Registrate</span>
      </button>
      */}

      <main className="app-main">

        {/* ── RECUADRO BLANCO-DEGRADADO ── */}
        <div className="form-card">

          <div className="instructions">
            <p>
              Para corroborar tu <strong>número de participación </strong> en el Sorteo Magisterial, ingresa tu {' '}
              <strong>RFC (con homoclave)</strong> o <strong>CURP</strong> sin espacios.
            
            </p>
          </div>

          <form onSubmit={handleSubmit} className="search-form">
            <input
              type="text"
              value={rfc}
              onChange={handleChange}
              onKeyPress={handleKeyPress}
              className="rfc-input"
              placeholder="XAXX010101X00 - XEXX010101HNEXXXA4"
              minLength={12}
              maxLength={18}
              required
              aria-label="Ingresa tu RFC o CURP"
              textAlign="center"
            />
            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
              style={{ backgroundColor: isLoading ? '#e37e1e' : '#344676' }}
            >
              {isLoading ? 'Validando...' : 'Validar Registro'}
            </button>
          </form>

          <button className="privacy-trigger" onClick={() => setShowPrivacy(true)}>
            Políticas de Privacidad
          </button>

        </div>
        {/* ── FIN RECUADRO ── */}

      </main>

      <Suspense fallback={null}>
        <Instructions isVisible={showInstructions} onClose={() => setShowInstructions(false)} />
        <PrivacyPolicy isVisible={showPrivacy} onClose={() => setShowPrivacy(false)} />
        <AddRecordForm
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleFormSubmit}
          municipios={catalogs.municipios}
          regiones={catalogs.regiones}
          nivelesEducativos={catalogs.niveles}
          isSubmitting={isLoading}
        />
      </Suspense>

      {messageOpen && (
        <Message
          onClose={handleModalClose}
          type={modalMessage.type}
          message={modalMessage.text}
          title={modalMessage.title}
          showContactInfoOnError={modalMessage.showContactInfo}
        />
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}

export default App;